import { DragDrop, DropEffect } from '@/components/drag-drop';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { LinkIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { ObjectParams } from '@/hooks/use-relation-insert';
import { useCreateContainer } from '@/features/containers/hooks/use-create-container';
import { useDeleteContainer } from '@/features/containers/hooks/use-delete-container';
import { useUpdateContainer } from '@/features/containers/hooks/use-update-container';
import { useDeleteAllObjectsFromContainer } from '@/features/containers/hooks/use-delete-all-objects-container';
import { useState } from 'react';
import prettyBytes from 'pretty-bytes';
import { Modal } from '@/components/modal';
import { ContainerData } from '@/features/containers/hooks/use-get-container';
import { Button } from '@/components/button/button';
import { createPortal } from 'react-dom';
import { useUserConfig } from '@/hooks/user/use-user-config';
import clsx from 'clsx';
import { Toggle } from '@/components/toggle';
import { useReindexContainer } from '@/features/containers/hooks/use-reindex-container';
import { set } from 'lodash';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';

interface ContainerFormInputs {
  name: string;
  schema: string;
  // alias: string;
  dynamic: boolean;
  reindex: boolean;
}

export function ContainerEditForm({
  data: containerData,
  onCreate,
  onNew,
  onDelete,
  widgetId,
}: {
  data?: ContainerData;
  onCreate: (uuid: string) => void;
  onNew?: () => void;
  onDelete?: () => void;
  widgetId: string;
}) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ContainerFormInputs>({
    defaultValues: {
      name: containerData?.title,
      // alias: containerData?.alias,
      dynamic: containerData?.dynamic || false,
      reindex: false,
    },
  });

  const { refetch: refetchUserConfig } = useUserConfig();
  const { mutate: createContainer, isLoading: isCreateLoading } =
    useCreateContainer();
  const { mutate: updateContainer, isLoading: isUpdateLoading } =
    useUpdateContainer();
  const { mutate: deleteContainer, isLoading: isDeleteLoading } =
    useDeleteContainer();
  const {
    mutate: deleteAllObjectsFromContainer,
    isLoading: isDeleteAllObjectsLoading,
  } = useDeleteAllObjectsFromContainer();
  const { mutate: reindexContainer, isLoading: isReindexing } = 
    useReindexContainer();
  const [reindexSuccess, setReindexSuccess] = useState<string | null>(null);
  const [reindexError, setReindexError] = useState<string | null>(null);

  function onSubmit(data: ContainerFormInputs) {
    if (containerData?.uuid) {
      updateContainer(
        {
          containerUuid: containerData.uuid,
          name: data.name,
          schemaUuid: data.schema,
          dynamic: data.dynamic,
          reindex: data.reindex,
        },
        {
          onSuccess: () => {
            refetchUserConfig();
          },
        }
      );
    } else {
      createContainer(
        {
          name: data.name,
          schemaUuid: data.schema,
          dynamic: data.dynamic,
        },
        {
          onSuccess: (response) => {
            onCreate(response.uuid);
            // setContainerAlias(response.alias);
            refetchUserConfig();
          },
        }
      );
    }
  }

  function handleDeleteClick() {
    setShowDeleteModal(true);
  }

  function handleDeleteCancelClick() {
    setShowDeleteModal(false);
  }

  function handleDeleteConfirmClick() {
    if (!containerData?.uuid) {
      return;
    }

    deleteContainer(
      { containerUuid: containerData.uuid },
      {
        onSuccess: () => {
          refetchUserConfig();
          reset();
          onDelete?.();
        },
      }
    );
  }

  function handleNewClick() {
    reset({
      name: '',
      // alias: '',
      dynamic: false,
      reindex: false,
    });
    onNew?.();
  }

  function handleDrop(
    { value }: { value: ObjectParams[] },
    field: keyof ContainerFormInputs
  ) {
    setValue(field, value[0].uuid);
  }

  function handleDeleteAllObjectsFromContainer() {
    if (!containerData?.uuid) {
      return;
    }

    deleteAllObjectsFromContainer(
      { containerUuid: containerData.uuid },
      {
        onSuccess: () => {},
      }
    );
  }

  function handleDragStart(
    event: React.DragEvent<HTMLButtonElement>,
    uuid: string,
    label: string
  ) {
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        type: 'object/insert',
        value: [{ uuid, type: 'container', label }],
      })
    );
    event.dataTransfer.dropEffect = DropEffect.Copy;
  }

  function handleReindexContainer({ containerUuid, schemaUuid }: { containerUuid: string; schemaUuid: string }) {
    setReindexSuccess(null);
    setReindexError(null);
    reindexContainer(
      { containerUuid, schemaUuid },
      {
        onSuccess: (data) => {
          console.log(data);
          if (!data.succeeded) {
            setReindexError(data.failReason);
          } else {
            setReindexSuccess('Reindexing completed successfully. Write operations are now permitted on the container.');
          }
          // setReindexStatus('Reindexing completed successfully.');
        },
      }
    );
  }

  const schema = watch('schema');

  const newButton = createPortal(
    <Button
      variant="tertiary"
      onClick={handleNewClick}
      className="py-1.5 bg-indigo-700 text-white hover:bg-indigo-500 focus:ring-indigo-500 focus:ring-offset-2 inline-flex items-center rounded-md border border-transparent px-2 text-sm font-medium focus:outline-none focus:ring-2"
    >
      New
    </Button>,
    document.getElementById(`widget-actions-${widgetId}`) || document.body
  );

  return (
    <>
      <form
        id="container-edit-form"
        className="grid gap-x-7 gap-y-4 items-start h-full"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="space-y-4">
          <p className="text-sm bg-gray-100 dark:bg-gray-700 rounded-md p-2 text-gray-700 dark:text-gray-400 text-center flex items-center justify-center gap-x-2">
            {containerData?.uuid ? (
              <>
                <span>You are editing container {containerData?.uuid}</span>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-1 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 cursor-move"
                  draggable
                  onDragStart={(event) =>
                    handleDragStart(
                      event,
                      containerData?.uuid,
                      containerData?.title
                    )
                  }
                >
                  <DocumentDuplicateIcon
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                    focusable={false}
                  />
                </button>
              </>
            ) : (
              'You are creating a new container.'
            )}
          </p>
          <div className="max-w-[400px]">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100"
            >
              Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="name"
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-400 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800"
                {...register('name', { required: true })}
              />
              {errors.name && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                  Name is required.
                </p>
              )}
            </div>
            <div className="mt-4">
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mr-2">
                  Dynamic Indexing
                </label>

                <Toggle
                  id="dynamic"
                  enabled={watch('dynamic')}
                  disabled={!!containerData}
                  label=""
                  onChange={() => setValue('dynamic', !watch('dynamic'))}
                  description="If enabled, the container will automatically map its content based on the defined schema."
                />
              </div>

              <p className="mt-2.5 text-sm text-gray-600 dark:text-gray-300">
                {watch('dynamic')
                  ? "Automatically indexes your data as it’s added. Useful for simple setups with only a few fields, but not ideal for more complex data."
                  : "You define how your data is indexed. This gives you more control and is better for larger or more complex data."}
              </p>
            </div>
            {/* {containerData?.alias && (
              <>
                <label
                  htmlFor="alias"
                  className="mt-2 block text-sm font-medium text-gray-900 dark:text-gray-100"
                >
                  Alias
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="alias"
                    disabled={true}
                    className={clsx(
                      "block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:focus:ring-indigo-400 sm:text-sm sm:leading-6 bg-white dark:bg-gray-800 cursor-not-allowed opacity-50",
                    )}
                    {...register('alias', { required: false })}
                  />
                </div>
              </>
            )} */}
          </div>
          <table className="w-full text-sm max-w-[400px]">
            <tbody>
              <tr>
                <td className="py-1 dark:text-gray-300">Size:</td>
                <td className="text-gray-500 dark:text-gray-400 text-right">
                  {containerData?.sizeBytes
                    ? prettyBytes(containerData?.sizeBytes)
                    : null}
                </td>
              </tr>
              <tr>
                <td className="py-1 align-top dark:text-gray-300">
                  Number of nodes:
                </td>
                <td className="text-right">
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-gray-500 dark:text-gray-400">
                      {containerData?.numberOfNodes}
                    </span>
                    {containerData?.numberOfNodes &&
                    containerData?.numberOfNodes > 0 ? (
                      <button
                        className="rounded-md bg-red-600 dark:bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 dark:hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:focus-visible:outline-red-500"
                        type="button"
                        onClick={handleDeleteAllObjectsFromContainer}
                      >
                        {isDeleteAllObjectsLoading ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Processing...
                          </>
                        ) : (
                          'Delete all objects'
                        )}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          {containerData ? (
            <div className="max-w-[400px]">
              <label
                htmlFor="schema"
                className="block text-sm font-medium text-gray-900 dark:text-gray-100"
              >
                Schema
              </label>
              {reindexSuccess && (
                <div className="mt-2 text-sm text-green-700 dark:text-green-300 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700 p-4">
                  <div className="font-medium mb-2 flex items-center text-green-800 dark:text-green-200">
                    <CheckCircleIcon className="h-5 w-5 inline-block mr-2 text-green-600 dark:text-green-400" />
                    Reindexing Succeeded
                  </div>
                  <p>{reindexSuccess}</p>
                </div>
              )}

              {reindexError && (
                <div className="mt-2 text-sm text-red-700 dark:text-red-300 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 p-4">
                  <div className="font-medium mb-2 flex items-center text-red-800 dark:text-red-200">
                    <XCircleIcon className="h-5 w-5 inline-block mr-2 text-red-600 dark:text-red-400" />
                    Reindexing Failed
                  </div>
                  <p>{reindexError}</p>
                </div>
              )}
              <div className="space-y-1 mt-2 mb-10">
                <DragDrop
                  allowedTypes={['object/insert']}
                  onDrop={(data) => handleDrop(data, 'schema')}
                  className="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-600 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-400 dark:hover:border-gray-500 border-dashed justify-center text-center h-5"
                  droppingClassName="relative flex items-center space-x-3 rounded-lg border border-gray-300 dark:border-gray-600 px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-600 dark:focus-within:ring-indigo-400 focus-within:ring-offset-2 hover:border-gray-800 dark:hover:border-gray-400 border-dashed bg-gray-50 dark:bg-gray-700 flex justify-center items-center h-5"
                >
                  {({ isDropping }) =>
                    isDropping ? (
                      <PlusIcon className="h-4 w-4 dark:text-gray-400" />
                    ) : (
                      <PlusIcon className="h-4 w-4 dark:text-gray-400" />
                    )
                  }
                </DragDrop>
                {schema ? (
                  <div className="relative flex items-center gap-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 pl-2 pr-3 shadow-sm text-xs">
                    <div className="relative w-10 h-10 bg-purple-50 dark:bg-purple-900/20 rounded-full flex justify-center items-center text-center">
                      <LinkIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {schema}
                      </p>
                    </div>
                    <button
                      onClick={() => setValue('schema', '')}
                      className="text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300"
                      type="button"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : null}

                <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
                  Apply schema changes to your data. This may take time and can affect how your data is stored and behaves. The container will be blocked from write operations until the process is complete. Use with caution, especially if you have a large amount of data or complex schemas.
                </p>
                
                <div className="flex justify-end">
                  <button
                    className="flex rounded-md bg-red-600 dark:bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 dark:hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:focus-visible:outline-red-500 mt-2.5"
                    type="button"
                    onClick={() => handleReindexContainer({ containerUuid: containerData?.uuid || '', schemaUuid: schema })}
                  >
                    {isReindexing ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </>
                    ) : (
                      'Reindex container'
                    )}
                  </button>
                </div>

              </div>
            </div>
          ) : null}
        </div>
        {newButton}
        <div className="flex gap-x-2 mt-auto">
          <Button
            variant="tertiary"
            loading={isCreateLoading || isUpdateLoading}
            type="submit"
          >
            Save
          </Button>
          {containerData?.uuid ? (
            <>
              <a
                href="#"
                className="inline-flex items-center rounded-md border border-transparent px-2 py-2 text-sm font-medium focus:outline-none focus:ring-2 bg-indigo-700 dark:bg-indigo-600 text-white hover:bg-indigo-500 dark:hover:bg-indigo-500 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2"
              >
                Navigate to
              </a>
              <button
                className="ml-auto rounded-md bg-red-600 dark:bg-red-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 dark:hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 dark:focus-visible:outline-red-500"
                type="button"
                onClick={handleDeleteClick}
              >
                {isDeleteLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Delete container'
                )}
              </button>
            </>
          ) : null}
        </div>
      </form>
      {showDeleteModal && (
        <Modal
          onCancel={handleDeleteCancelClick}
          onSubmit={handleDeleteConfirmClick}
          loading={isDeleteLoading}
          title="Delete container"
          message="Are you sure you want to delete this container? This action cannot be undone."
          actionName="Delete"
        />
      )}
    </>
  );
}
